import React, { useState, useEffect } from 'react';
import { ETIDialog, ETIDialogFooter, Button } from '@zawarski/eti-typescript-components';
import IntroTitle from '@/components/fragment/IntroTitle';
import FadeTransition from '@/components/fragment/FadeTransition';
import { SetupView } from './components';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { TOGGLE_SELECTED_PROGRAM } from '@/store/app/app.actions';
import { programColumnsSelector } from '@/store/programs/programs.selector';
import { updateDashboardSettings } from '@/store/account/account.thunk';
import { updateProgramsHistoryArray } from '@/utils';
import { useMediaQuery } from '@react-hook/media-query';
import { BREAKPOINT_700PX } from '@/common/const/breakpoints';
import { progInFullScreenSelector } from '@/store/app/app.selector';
import palmettoDashboardGradient from '@/assets/images/palmettoDashboardGradient.png';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import whatsNewData from '@/data/whatsNewData.json';

type OnBoardingViewProps = {
  isInitialOnBoarding: boolean;
  open: boolean;
  onClose: () => void;
  onBoardingSettings: {
    isInitialized: boolean;
    hasUpdates: boolean;
    hasCheckedHide: boolean;
    programInFullScreen: string;
  };
  programsList: Nullable<ISelectedPrograms[]>;
  programsHistory: string[];
};

interface FeatureUpdate {
  title: string;
  updates: string[];
}

interface WhatsNewData {
  date: string;
  features: FeatureUpdate[];
}

interface TestJsonData {
  data: WhatsNewData[];
}

interface ListViewTestJsonProps {
  data: TestJsonData;
}

const OnBoardingView: React.FC<OnBoardingViewProps> = ({
  isInitialOnBoarding,
  open,
  onClose,
  onBoardingSettings,
  programsList,
  programsHistory,
}) => {
  const progInFullScreen = useAppSelector(progInFullScreenSelector);
  const dispatch = useAppDispatch();
  const columns = useAppSelector(programColumnsSelector);
  const [stepState, setStepState] = useState<0 | 1>(0);
  const [hasMadeChanges, setHasMadeChanges] = React.useState(false);
  const [displayMode, setDisplayMode] = React.useState('');
  const [stepper, setStepper] = useState<0 | 1 | 2 | 3>(0);
  const [showWhatsNew, setShowWhatsNew] = useState<boolean>(false);
  const [toggledPrograms, setToggledPrograms] = useState<string[]>([]);
  const isMobile = useMediaQuery(`only screen and (max-width: ${BREAKPOINT_700PX})`);

  useEffect(() => {
    if (!onBoardingSettings) return;
    const displayMode = onBoardingSettings?.isInitialized
      ? progInFullScreen
        ? 'fullscreen'
        : 'columns'
      : '';
    setDisplayMode(displayMode);
  }, [onBoardingSettings, progInFullScreen]);

  const updateSettings = (newOnboardingSettings: OnBoarding) => {
    if (Object.keys(columns).length > 0) {
      const selectedPrograms = programsList
        .filter((program) => program.isActive)
        ?.map((program) => program.id.toString());

      const updatedHistory = updateProgramsHistoryArray(selectedPrograms, programsHistory);

      dispatch(
        updateDashboardSettings({
          selectedPrograms: columns,
          onBoarding: newOnboardingSettings,
          programsHistory: updatedHistory,
        })
      );
    } else {
      dispatch(updateDashboardSettings({ onBoarding: newOnboardingSettings }));
    }
  };

  const handleNext = (step: 0 | 1) => {
    setStepState(step);
  };

  const nextStep = (step: 0 | 1 | 2 | 3) => {
    setStepper(step);
  };

  const onToggleProgram = (id: number | string) => {
    setToggledPrograms((prev) => {
      if (prev.includes(id.toString())) {
        return prev.filter((item) => item !== id.toString());
      } else {
        return [...prev, id.toString()];
      }
    });
    dispatch(TOGGLE_SELECTED_PROGRAM(id));
  };

  const onCloseResetOnboarding = () => {
    if (toggledPrograms.length) {
      toggledPrograms.forEach((program) => {
        dispatch(TOGGLE_SELECTED_PROGRAM(Number(program)));
      });
    }
    setToggledPrograms([]);
    onClose();
    setStepState(0);
    nextStep(0);
  };

  const ProgressBar = ({ stepper }: { stepper: 0 | 1 | 2 | 3 }) => {
    const progress = () => {
      switch (stepper) {
        case 0:
          return 33;
        case 1:
          return 66;
        case 2:
          return 100;
        default:
          return 0;
      }
    };

    const progressValue = progress();

    return (
      <div className='w-full mb-2'>
        <div className='mb-1 text-sm font-medium text-vulcan-600 text-center'>
          Progress: {stepper + 1}/3
        </div>
        <div className='bg-white-100 w-full rounded-full h-4'>
          <div
            className='bg-vulcan-600 h-full rounded-full transition-all duration-300'
            style={{ width: `${progressValue}%` }}
          />
        </div>
      </div>
    );
  };

  const ListViewWithData = ({ data }: ListViewTestJsonProps) => {
    return (
      <div className={'flex flex-col gap-2'}>
        {data.data.map((item, itemIndex) => (
          <div key={itemIndex} className={'flex flex-col p-4 bg-white-50'}>
            <div className={'text-sm text-vulcan-600 font-medium mb-3'}>{item.date}</div>
            {item.features.map((feature, index) => (
              <div key={index} className={'mb-4 last:mb-0'}>
                <h3 className={'font-semibold text-[14px] leading-[120%] tracking-[0] mb-2 text-lynch-500'}>{feature.title}</h3>
                <ul className={'space-y-1'}>
                  {feature.updates.map((update, updateIndex) => (
                    <li key={updateIndex} className={'font-400 text-[14px] text-lynch-500 ml-4'}>
                      {update}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const WelcomeView = () => {
    return (
      <div className={`flex flex-col ${isMobile ? 'p-2' : 'p-0 gap-6'}`}>
        <div className={'flex w-full justify-center'}>
          <img
            className={'object-contain w-[100px]'}
            src={palmettoDashboardGradient}
            alt={'palmettoDashboardGradient'}
          />
        </div>
        <div className="flex-shrink-0">
          <IntroTitle
            title={'Welcome'}
            subtitle={
              'Let’s setup your Palmetto interface. Your preferences can be adjusted later using the “Onboarding” feature in the Dashboard’s side panel.'
            }
          />
        </div>
        <div className="flex-shrink-0">
          <ETIDialogFooter
            sx={{ padding: '0px' }}
            children={[
              <div className={'flex justify-between w-full p-0 gap-6'} key={'footerdiv'}>
                <Button color={'secondary'} onClick={() => setShowWhatsNew(true)}>
                  SEE WHAT'S NEW
                </Button>
                <Button onClick={() => handleNext(1)}>START SETUP</Button>
              </div>,
            ]}
          />
        </div>
      </div>
    );
  };

  const View = () => {
    switch (stepState) {
      case 0:
        return showWhatsNew ? <WhatsNew /> : <WelcomeView />;
      case 1:
        return (
          <SetupView
            isInitialOnBoarding={isInitialOnBoarding}
            programLists={programsList}
            stepper={stepper}
            nextStep={nextStep}
            onClose={onClose}
            onToggleProgram={onToggleProgram}
            updateSettings={updateSettings}
            onBoardingSettings={onBoardingSettings}
            setStepState={setStepState}
            isMobile={isMobile}
            hasMadeChanges={hasMadeChanges}
            setHasMadeChanges={setHasMadeChanges}
            displayMode={displayMode}
            setDisplayMode={setDisplayMode}
            progInFullScreen={progInFullScreen}
            onCloseResetOnboarding={onCloseResetOnboarding}
          />
        );
      default:
        return null;
    }
  };

  const WhatsNew = () => {
    return (
      <div className={`flex flex-col w-[550px] min-h-[654px] ${isMobile ? 'p-2' : 'p-0'}`}>
        <IntroTitle
          title={`What's New`}
          subtitle={
            'We are always working on improvements. Details about new versions and updates available below.'
          }
        />
        <div className={'flex flex-col gap-2.5 overflow-y-auto mt-6'}>
          <ListViewWithData data={whatsNewData} />
        </div>
        <FadeTransition />
        <ETIDialogFooter
          sx={{ padding: '0px', marginTop: '24px' }}
          children={[
            <div className={'flex justify-between w-full p-0'}>
              <Button
                className={'!bg-lynch-100 !text-lynch-900'}
                color={'secondary'}
                onClick={() => setShowWhatsNew(false)}
                startIcon={<ArrowBackIosIcon />}>
                GO BACK
              </Button>
            </div>,
          ]}
        />
      </div>
    );
  };

  return (
    <ETIDialog
      open={open}
      className={'bg-gradient-to-b from-[#0031CC]/40 to-vulcan-500/20'}
      children={[
        <div
          className={`flex flex-col ${isMobile ? 'p-4' : 'p-8 gap-6'} rounded-xl bg-lynch-50`}
          key={'dialogOnboard'}>
          {stepState === 0 && (
            <div className={isInitialOnBoarding ? 'hidden' : 'flex justify-end items-center'}>
              <IconButton size='large' onClick={onClose} sx={{ padding: 0 }}>
                <CloseIcon fontSize='inherit' />
              </IconButton>
            </div>
          )}
          <View />
          {/*  PROGRESS BAR */}
          {stepState === 1 && stepper !== 3 && <ProgressBar stepper={stepper} />}
        </div>,
      ]}
    />
  );
};

export default OnBoardingView;
